/**
 * Which permission each page needs. Every route in src/routes must be listed —
 * access.test.ts fails otherwise, so a new page can't be forgotten.
 *
 * 'public' pages work signed out. The rest need a session and the permission.
 * The rule Gideon set: everything under /settings is admin-only.
 */
import type { Permission } from './permissions.ts'

export type RouteAccess = 'public' | Permission

export const routeAccess: Record<string, RouteAccess> = {
  '/': 'app.use',
  '/account': 'app.use',
  // Majik's sections — everyone who uses the app sees all data (docs/majik).
  '/jobs': 'app.use',
  '/jobs/$jobId': 'app.use',
  '/clients': 'app.use',
  '/clients/$clientId': 'app.use',
  '/messages': 'app.use',
  '/schedule': 'app.use',
  '/cleaners': 'app.use',
  '/cleaners/$cleanerId': 'app.use',
  '/inspections': 'app.use',
  '/invoices': 'app.use',
  '/invoices/$invoiceId': 'app.use',
  '/orders': 'app.use',
  '/orders/$orderId': 'app.use',
  '/inventory': 'app.use',
  '/settings': 'settings.manage',
  '/settings/users': 'users.manage',
  '/settings/system': 'system.view',
  // MCP access: the switch, every connection and token, the usage log.
  '/settings/mcp': 'settings.manage',
  // Where an AI app sends someone to approve it (docs/MCP.md).
  '/oauth/consent': 'mcp.use',
  // Where connecting an AI app lands when it goes wrong — maybe signed out.
  '/oauth/error': 'public',
  '/settings/data-dictionary': 'settings.manage',
  // Go live, the business rules, who gets which alert, connections.
  '/settings/business': 'settings.manage',
  // Import from Monday: add the exports, check, import (docs/majik/DATA-MODEL-V1.md §7).
  '/settings/import': 'settings.manage',
  '/settings/webhooks': 'settings.manage',
  '/setup': 'public',
  '/sign-in': 'public',
  '/sign-up': 'public',
  '/forgot-password': 'public',
  '/reset-password': 'public',
  '/accept-invitation/$id': 'public',
  '/create-account': 'public',
  '/confirm-email': 'public',
}

/**
 * The permission for a URL path: the longest listed route that matches it, so
 * /settings/anything-new is admin-only even before it is listed.
 */
export function accessForPath(pathname: string): RouteAccess {
  const path = pathname.replace(/\/+$/, '') || '/'
  let best: { length: number; access: RouteAccess } | null = null
  for (const [route, access] of Object.entries(routeAccess)) {
    const pattern = new RegExp(`^${route.replace(/\$[^/]+/g, '[^/]+')}(/.*)?$`)
    const matches = route === '/' ? path === '/' : pattern.test(path)
    if (matches && (!best || route.length > best.length)) best = { length: route.length, access }
  }
  // A path nobody declared needs the ordinary sign-in permission.
  return best?.access ?? 'app.use'
}
